// src/client/routes/_app/projects.$id.tsx
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/client/components/ui/alert-dialog';
import { ProjectDialog } from '@/client/components/projects/ProjectDialog';
import { ProjectDetailHeader } from '@/client/components/projects/ProjectDetailHeader';
import { ProjectDetailStatCards } from '@/client/components/projects/ProjectDetailStatCards';
import { ProjectTeamCard } from '@/client/components/projects/ProjectTeamCard';
import { ProjectInfoCard } from '@/client/components/projects/ProjectInfoCard';
import { ProjectTimeTable } from '@/client/components/projects/ProjectTimeTable';
import type { ProjectFormState } from '@/client/components/projects/ProjectFormFields';
import { trpc } from '@/client/utils/trpc';

export const Route = createFileRoute('/_app/projects/$id')({
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { data: me } = trpc.user.me.useQuery();
  const isAdmin = me?.role === 'ADMIN';

  const { data: project, isLoading } = trpc.projects.byId.useQuery({ id });
  const { data: employees = [] } = trpc.employees.list.useQuery(undefined, {
    enabled: isAdmin === true,
  });
  const { data: timeSummary = [] } = trpc.projects.getMonthlyTimeSummary.useQuery(
    { id },
    { enabled: isAdmin === true },
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [managingOpen, setManagingOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const updateMutation = trpc.projects.update.useMutation({
    onSuccess: () => {
      utils.projects.byId.invalidate({ id });
      utils.projects.list.invalidate();
      setDialogOpen(false);
    },
    onError: (e) => setFormError(e.message),
  });
  const removeMutation = trpc.projects.remove.useMutation({
    onSuccess: () => navigate({ to: '/projects' }),
  });
  const addMemberMutation = trpc.projects.addMember.useMutation({
    onSuccess: () => utils.projects.byId.invalidate({ id }),
  });
  const removeMemberMutation = trpc.projects.removeMember.useMutation({
    onSuccess: () => utils.projects.byId.invalidate({ id }),
  });
  const setMemberRateMutation = trpc.projects.setMemberRate.useMutation({
    onSuccess: () => utils.projects.byId.invalidate({ id }),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!project) return <p className="text-sm text-muted-foreground">Project not found.</p>;

  const currentMemberIds = new Set(project.members.map((m) => m.id));
  const availableEmployees = employees.filter((e) => !currentMemberIds.has(e.id));

  const monthLabel = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  function handleProjectSubmit(form: ProjectFormState) {
    setFormError(null);
    updateMutation.mutate({
      id,
      name: form.name,
      description: form.description || undefined,
      status: form.status,
      client_name: form.client_name || undefined,
    });
  }

  return (
    <div className="space-y-6">
      <Link
        to="/projects"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        Projects
      </Link>

      <ProjectDetailHeader
        project={project}
        isAdmin={isAdmin ?? false}
        onEdit={() => {
          setFormError(null);
          setDialogOpen(true);
        }}
        onDelete={() => setDeleteOpen(true)}
      />

      <ProjectDetailStatCards memberCount={project.members.length} createdAt={project.created_at} />

      <div className="grid gap-4" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
        <ProjectTeamCard
          members={project.members}
          availableEmployees={availableEmployees}
          isAdmin={isAdmin ?? false}
          managingOpen={managingOpen}
          onManagingOpenChange={setManagingOpen}
          onAddMember={(employeeId) =>
            addMemberMutation.mutate({ project_id: id, employee_id: employeeId })
          }
          onRemoveMember={(employeeId) =>
            removeMemberMutation.mutate({ project_id: id, employee_id: employeeId })
          }
          isAddPending={addMemberMutation.isPending}
          isRemovePending={removeMemberMutation.isPending}
          onSetMemberRate={(employeeId, rate) =>
            setMemberRateMutation.mutate({ project_id: id, employee_id: employeeId, rate })
          }
          isSetRatePending={setMemberRateMutation.isPending}
        />
        <ProjectInfoCard project={project} />
      </div>

      {isAdmin && <ProjectTimeTable entries={timeSummary} monthLabel={monthLabel} />}

      <ProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        project={project}
        onSubmit={handleProjectSubmit}
        isPending={updateMutation.isPending}
        error={formError}
      />

      <AlertDialog open={deleteOpen} onOpenChange={(o) => !o && setDeleteOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &quot;{project.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the project and all member assignments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={removeMutation.isPending}
              onClick={() => removeMutation.mutate({ id })}
            >
              {removeMutation.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
